import { DOCUMENT, effect, inject, Injectable, NgZone, RendererFactory2, signal } from '@angular/core';
import { App, AppState, URLOpenListenerEvent } from '@capacitor/app';
import { AvailableResult, BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric';
import { TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { from, Observable, of } from 'rxjs';
import packageInfo from 'package.json';
import { map, catchError } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { UserDataService } from 'src/data-store/user-data.service';
import { EApiDevicePlatforms } from 'src/models/app.model';
import { RestApiService } from './rest-api.service';
import { Capacitor } from '@capacitor/core';
import { PrivacyScreen } from '@capacitor/privacy-screen';
import { Router } from '@angular/router';
import { NO_SCROLL_CLASS } from 'src/util/constants';

@Injectable({
  providedIn: 'root'
})
export class PhoneNativeService {
  private translateService = inject(TranslateService);
  private userDataService = inject(UserDataService);
  private loggerService = inject(LoggerService);
  private restApiService = inject(RestApiService);
  private zone = inject(NgZone);
  private router = inject(Router);
  private readonly document = inject<Document>(DOCUMENT);

  private lastTimeSuccessfullyChecked!: Dayjs | null;
  private lastTimeActive: Dayjs | null = null; // additional check to prevent BI-413
  private biometryChecking = false;
  public biometryLocked = signal<boolean>(false);

  private renderer = inject(RendererFactory2).createRenderer(null, null);

  public constructor() {
    window.addEventListener('click', this.updateLastActive.bind(this));
    window.addEventListener('scroll', this.updateLastActive.bind(this));
    window.addEventListener('touch', this.updateLastActive.bind(this));

    effect(() => {
      this.setBodyScrollLocked(this.biometryLocked());
    })
  }

  private updateLastActive(): void {
    this.lastTimeActive = dayjs();
  }

  public enableScreenBlurOnBackground(): void {
    App.addListener('appStateChange', this.appStateChange.bind(this));
    // init biometric call when app is restarted
    this.appStateChange({isActive: true});
    if(Capacitor.getPlatform() === 'android') {
      PrivacyScreen.enable({
        android: {
          dimBackground: true,
          privacyModeOnActivityHidden: 'splash',
          /**
           * @Deprecated
           * FLAG_SECURE is workarounded in MainActivity.java for Android env.
           * Look for WindowManager.LayoutParams.FLAG_SECURE usage.
           */
          preventScreenshots: false,
        },
      });
    }
  }

  public initDeeplinksWatcher(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(event.url);
        const slug = url.pathname + url.search + url.hash;
        this.router.navigateByUrl(slug);
      });
    });
  }

  private appStateChange(state: AppState) {
    // hide app preview when inactive, only works on iOS
    if(!state.isActive) {
      this.biometryLocked.set(true);
    }

    from(NativeBiometric.isAvailable({ useFallback: true })).subscribe({
      next: (result: AvailableResult) => {
        // todo - return if !translationsLoaded
        if(
          this.userDataService.loggedUser()
          && state.isActive
          && (!this.lastTimeSuccessfullyChecked
            || dayjs().diff(this.lastTimeSuccessfullyChecked, 'minutes') >= 2)
          && result.biometryType !== BiometryType.NONE
          && (!this.lastTimeActive
            || dayjs().diff(this.lastTimeActive, 'minutes') >= 2)
        ) {
          this.biometricAuth();
        } else if(state.isActive) {
          this.biometryLocked.set(false);
          this.lastTimeSuccessfullyChecked = null;
        }
      },
      error: (err) => {
        // log unexpected biometric error but dont deny user from app access
        this.loggerService.error('Biometric error (3)', err);
        this.biometryLocked.set(false);
      },
    });
  }

  public biometricAuth(): void {
    if(this.biometryChecking) return;
    this.biometryChecking = true;

    this.biometryLocked.set(true);

    from(NativeBiometric.verifyIdentity({
      reason: this.translateService.instant('nativePhoneAuth.reason'),
      title: this.translateService.instant('nativePhoneAuth.reason'),
      useFallback: true,
    })).subscribe({
      next: () => this.biometricSuccessResult(),
      error: (err) => this.biometricResultError(err), // failed auth = error callback
    });
  }

  private biometricSuccessResult(): void {
    this.biometryChecking = false;
    this.lastTimeActive = dayjs();
    this.lastTimeSuccessfullyChecked = dayjs();
    this.biometryLocked.set(false);
  }

  private biometricResultError(error: unknown): void {
    this.biometryChecking = false;

    try {
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        'message' in error &&
        typeof error['code'] === 'string'
      ) {
        //check capacitor-native-biometric/BiometricAuthError for other codes
        const shouldRecheckCodes = [
          '10', // print mismatch
          '16', // user cancel
        ];
        // do not log to sentry/expected fails
        const dontLogCodes = [
          '3', // no available biometrics
          '13', // not interactive - too many failed attempts or locked device
          '15', // system cancel (ex. app move to background)
        ];

        if(error['code'] && shouldRecheckCodes.includes(error['code'])) {
          this.lastTimeSuccessfullyChecked = null;
          this.biometricAuth();
        } else if(error['code'] && dontLogCodes.includes(error['code'])) {
          this.lastTimeSuccessfullyChecked = null;
        } else {
          this.loggerService.error('Biometric error (1)', error);
          this.biometryLocked.set(false);
        }
      } else {
        this.loggerService.error('Unknown biometric error', error);
        this.biometryLocked.set(false);
      }
    } catch (error) {
      this.loggerService.error('Biometric error (2)', error);
      this.biometryLocked.set(false);
    }
  }

  // todo - move to separated file/service from native checking
  public isVersionValid(device: EApiDevicePlatforms | null): Observable<boolean> {
    // All error cases determine valid as TRUE not to block the user's version due to server issues etc.

    if (!device) {
      this.loggerService.error('Only android or ios devices are allowed!');
      return of(true)
    }

    let appVersion: string;
    try {
      appVersion = packageInfo.version;
      this.loggerService.logInfo(appVersion);
    } catch (error) {
      this.loggerService.error('Failed to set app version!', error);
      return of(true);
    }

    const regex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
    const validVersionFormat = regex.test(appVersion);

    if (!appVersion || !validVersionFormat) {
      this.loggerService.error('App version set to invalid value: ', appVersion);
      return of(true)
    }
    return this.restApiService.validatePhoneAppVersion(device, appVersion).pipe(
      map(res => res.valid),
      catchError(err => {
        this.loggerService.error('Could not retrieve app version validation from the server, app version: ' + appVersion + ', error: ', err ? err : 'general');
        return of(true);
      })
    );
  }

  public setBodyScrollLocked(locked: boolean): void {
    const body = this.document?.body;
    if (!body) {
      return;
    }

    if (locked) {
      this.renderer.addClass(body, NO_SCROLL_CLASS);
    } else {
      this.renderer.removeClass(body, NO_SCROLL_CLASS);
    }
  }
}
